<script lang="ts">
  import { mdiEyeOutline, mdiEyeCheckOutline } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ToggleCTA from "@kontextwerk/web-sdk/ui/actions/ToggleCTA.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import ModalDialog from "@kontextwerk/web-sdk/ui/overlay/ModalDialog.svelte"

  import {
    scheduleEventActionValidationCancelLabel,
    presentScheduleEventActionIssue,
    scheduleEventActionValidationTitle,
    type ScheduleEventActionValidationContext,
  } from "../../../../_shared/_logic/action-validation-presentation"

  type Props = {
    issues: ScheduleEventActionValidationIssue[]
    context: ScheduleEventActionValidationContext
    processing?: boolean
    onConfirm: () => void
    onCancel: () => void
  }

  const { issues = [], context, processing = false, onConfirm, onCancel }: Props = $props()

  const unreadValue = "unread"
  const readValue = "read"
  const readOptions = [
    { value: unreadValue, label: "Nicht gelesen", icon: mdiEyeOutline },
    { value: readValue, label: "Gelesen", icon: mdiEyeCheckOutline },
  ]
  const readAllHint = "Bitte bestätigen Sie jeden Hinweis mit Gelesen."

  let readState = $state<Record<string, string>>({})

  const title = $derived(scheduleEventActionValidationTitle(context))
  const cancelLabel = $derived(scheduleEventActionValidationCancelLabel(context))
  const presentedIssues = $derived(
    issues.map((issue) => presentScheduleEventActionIssue(issue, context)),
  )
  const issueKeys = $derived(
    presentedIssues.map(
      (issue, index) => `${index}:${issues[index]?.code ?? "unknown"}:${issue.title}`,
    ),
  )
  const allIssuesRead = $derived(issueKeys.every((key) => readState[key] === readValue))
  const canConfirm = $derived(allIssuesRead && !processing)

  $effect(() => {
    const keys = issueKeys
    const nextState: Record<string, string> = {}
    let changed = Object.keys(readState).length !== keys.length
    for (const key of keys) {
      nextState[key] = readState[key] ?? unreadValue

      if (!(key in readState)) changed = true
    }

    if (changed) readState = nextState
  })

  const setReadState = (key: string, value: string) => {
    readState = { ...readState, [key]: value }
  }

  const confirmIfReady = () => {
    if (!canConfirm) return

    onConfirm()
  }

  const issueTag = (severity: ScheduleEventActionValidationIssue["severity"]) =>
    severity === "consequence" ? "Folge" : "Hinweis"

  const issueTagColor = (severity: ScheduleEventActionValidationIssue["severity"]) =>
    severity === "consequence" ? "red" : "yellow"
</script>

<ModalDialog appearance="plain" {title} size="large" closeLabel={cancelLabel} onClose={onCancel}>
  {#snippet content()}
    <div class="schedule-event-action-validation">
      <div class="issue-list">
        {#each presentedIssues as issue, index}
          {@const issueKey = issueKeys[index]}
          <div class="issue-card">
            <ActionBox
              label={issue.title}
              darkMode={false}
              brightBorder={false}
              tag={issueTag(issue.severity)}
              tagColor={issueTagColor(issue.severity)}
              tagVariant="soft"
            >
              <dl class="issue-copy">
                <div>
                  <dt>Was ist los?</dt>
                  <dd>{issue.problem}</dd>
                </div>
                <div>
                  <dt>Wenn Sie jetzt anwenden:</dt>
                  <dd>{issue.consequence}</dd>
                </div>
              </dl>
              {#snippet buttonSnippet()}
                <ToggleCTA
                  options={readOptions}
                  value={readState[issueKey] ?? unreadValue}
                  darkMode={false}
                  disabled={processing}
                  small={true}
                  dataTestid={`schedule-event-action-validation.issue.${index}.read-state`}
                  onChange={(value) => setReadState(issueKey, value)}
                />
              {/snippet}
            </ActionBox>
          </div>
        {/each}
      </div>
      {#if !allIssuesRead}
        <p class="read-hint">{readAllHint}</p>
      {/if}
    </div>
  {/snippet}

  {#snippet footerSnippet()}
    <div class="actions">
      <CTA
        darkMode={false}
        type="secondaryButton"
        content={cancelLabel}
        onClick={onCancel}
        disabled={processing}
      />
      <CTA
        darkMode={false}
        type="primaryButton"
        content={processing ? "Wird angewendet..." : "Jetzt anwenden"}
        onClick={confirmIfReady}
        disabled={!canConfirm}
        tooltipContent={!allIssuesRead ? readAllHint : ""}
      />
    </div>
  {/snippet}
</ModalDialog>

<style>
  .schedule-event-action-validation {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    min-width: 0;
  }

  .issue-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 19rem), 1fr));
    gap: 0.85rem;
    align-items: stretch;
  }

  .issue-card {
    min-width: 0;
  }

  .issue-card :global(.box) {
    min-height: 100%;
  }

  .issue-copy {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    margin: 0;
    color: var(--text-invers-100);
  }

  .issue-copy dt {
    color: var(--text-invers-100);
    font-weight: 700;
    margin-bottom: 0.15rem;
  }

  .issue-copy dd {
    color: color-mix(in srgb, var(--text-invers-100) 78%, transparent);
    margin: 0;
    line-height: 1.45;
  }

  .read-hint {
    color: color-mix(in srgb, var(--text-invers-100) 78%, transparent);
    font-size: 0.9rem;
    margin: 0;
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.5rem;
  }
</style>
